import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Loader2, IndianRupee, DollarSign, Building2, Clock } from "lucide-react";

export default function PlatformAdminBilling() {
  const [kpis, setKpis] = useState(null);
  const [queue, setQueue] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("queue");

  async function load() {
    try {
      const [k, q, o] = await Promise.all([
        api.get("/platform-admin/billing/kpis"),
        api.get("/platform-admin/billing/queue"),
        api.get("/platform-admin/billing/orgs"),
      ]);
      setKpis(k.data); setQueue(q.data.items || []); setOrgs(o.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function verify(id) {
    setBusy(id + "v");
    try { const { data } = await api.post(`/platform-admin/billing/payments/${id}/verify`); toast.success(data.message); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }
  async function reject(id) {
    const reason = window.prompt("Reason for rejecting this payment (emailed to the customer):");
    if (!reason || reason.trim().length < 3) return;
    setBusy(id + "r");
    try { const { data } = await api.post(`/platform-admin/billing/payments/${id}/reject`, { reason: reason.trim() }); toast.success(data.message); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }

  if (!kpis) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="platform-billing-root" className="max-w-6xl mx-auto px-6 py-10">
      <h1 className="font-heading text-3xl tracking-tight">Platform billing</h1>
      <p className="text-sm text-muted-foreground mt-1">Revenue, UPI verification queue and organisations.</p>

      <div className="grid sm:grid-cols-4 gap-4 mt-6">
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-revenue-inr">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><IndianRupee className="w-4 h-4" /> Revenue (India)</div>
          <div className="mt-2 font-heading text-2xl">{kpis.revenue_inr_display}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-revenue-usd">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><DollarSign className="w-4 h-4" /> Revenue (Intl)</div>
          <div className="mt-2 font-heading text-2xl">{kpis.revenue_usd_display}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-orgs">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Building2 className="w-4 h-4" /> Organisations</div>
          <div className="mt-2 font-heading text-2xl">{kpis.orgs_active}/{kpis.orgs_total}</div>
          <div className="text-xs text-muted-foreground mt-1">active / total</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="kpi-pending">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Clock className="w-4 h-4" /> Pending verify</div>
          <div className="mt-2 font-heading text-2xl" style={{ color: kpis.pending_verifications ? "hsl(var(--ink-warning))" : "hsl(var(--navy))" }}>{kpis.pending_verifications}</div>
        </div>
      </div>

      <div className="inline-flex rounded-full border border-border p-1 bg-card mt-8">
        {[["queue", `Verification queue (${queue.length})`], ["orgs", `Organisations (${orgs.length})`]].map(([v, l]) => (
          <button key={v} data-testid={`pa-tab-${v}`} onClick={() => setTab(v)} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === v ? "text-white" : "text-foreground/70"}`} style={tab === v ? { background: "hsl(var(--navy))" } : {}}>{l}</button>
        ))}
      </div>

      {tab === "queue" ? (
        <div className="mt-4 rounded-2xl border border-border bg-card overflow-x-auto">
          {queue.length === 0 ? <div className="p-10 text-center text-sm text-muted-foreground">No payments waiting for verification.</div> : (
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
                <th className="px-4 py-3">Organisation</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">UTR</th><th className="px-4 py-3 text-right">Action</th>
              </tr></thead>
              <tbody>
                {queue.map((p) => (
                  <tr key={p.id} data-testid="pa-queue-row" className="border-t border-border">
                    <td className="px-4 py-3">{p.org_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.order_ref}</td>
                    <td className="px-4 py-3">{p.order?.plan_name} · {p.order?.billing_cycle}</td>
                    <td className="px-4 py-3 font-semibold">{p.amount_display}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.utr}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button data-testid="pa-verify-btn" onClick={() => verify(p.id)} disabled={!!busy} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60" style={{ background: "hsl(var(--cyan))" }}>{busy === p.id + "v" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Verify</button>
                        <button data-testid="pa-reject-btn" onClick={() => reject(p.id)} disabled={!!busy} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border border-border disabled:opacity-60"><X className="w-3.5 h-3.5" /> Reject</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
              <th className="px-4 py-3">Organisation</th><th className="px-4 py-3">Region</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Valid until</th>
            </tr></thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id} data-testid="pa-org-row" className="border-t border-border">
                  <td className="px-4 py-3">{o.name}</td>
                  <td className="px-4 py-3">{o.region === "IN" ? "India" : "International"}</td>
                  <td className="px-4 py-3">{o.plan_name || "—"}</td>
                  <td className="px-4 py-3 capitalize">{(o.status || "").replace("_", " ")}</td>
                  <td className="px-4 py-3">{o.valid_until || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
